import {
  EnrichmentDetail,
  resolveLocalized,
} from "@app/shared/models/enrichment-detail.model";

/** A localized field that is still blank for a given locale — surfaced under the
 *  language coverage widget so the user can see (and jump to) what's missing. */
export interface MissingField {
  label: string;
  /** Canonical tab id that edits this field (for the jump). */
  tabId: string;
  /** Optional `data-readiness-target` sectionId to scroll to. */
  sectionId?: string;
  /** Optional `data-readiness-target` fieldKey to scroll to. */
  fieldKey?: string;
}

/** Per-language fill stats over every localized field in the flat payload. */
export interface FlatLangCoverage {
  /** Canonical locale, e.g. "th-TH". */
  locale: string;
  filled: number;
  total: number;
  /** 0–100. `total === 0` → 0. */
  percent: number;
  /** The fields still blank for this locale (empty when 100%). */
  missing: MissingField[];
}

/** A BCP-47-ish locale key (`th-TH`, `en-US`, `zh-CN`, `km-KH`, `my-MM`). */
const LOCALE_KEY = /^[a-z]{2,3}-[A-Za-z]{2,4}$/;

/** Keys dropped at ANY depth of the walk because they carry no user-editable
 *  localized content:
 *   - `__name`: the FE-only shelf-name mirror. Shelf names are catalog-owned
 *     (the user only adds/removes shelves), so a locale gap here can't be
 *     filled — counting it made 100% unreachable.
 *   - `tax`: tax lines are read-only in the Pricing editor (the catalog owns
 *     them), so their currency bags aren't user-fillable. */
const NON_CONTENT_KEYS: ReadonlySet<string> = new Set<string>([
  "__name",
  "tax",
]);

/** Pinned service displays that MIRROR the price name/description — they are not
 *  editable as their own fields (the Pricing tab edits `prices[i].name` /
 *  `.description` directly), so counting their locale bags double-counts and
 *  shows a "still blank" entry for a field the user already filled elsewhere. */
const PINNED_DISPLAY_NAMES: ReadonlySet<string> = new Set<string>([
  "displayProductName",
  "displayProductDescription",
]);

/** The promo-badge display. Its label counts only when the badge is Active. */
const BADGE_DISPLAY_NAME = "displayProductBadge";

/** Offering-level fields that are rendered READ-ONLY (no editor anywhere in the
 *  workflow — the Overview summary just displays them). A locale gap on these
 *  can never be filled by the user, so counting them made 100% impossible.
 *  Skipped only at the TOP level (nested `prices[i].name/description` are real,
 *  editable fields and stay counted). */
const NON_CONTENT_TOP_LEVEL: ReadonlySet<string> = new Set<string>([
  "name",
  "description",
]);

/** True when `v` is a locale→value map (object with ≥1 locale-shaped key). */
function isLocaleBag(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return Object.keys(v as Record<string, unknown>).some((k) =>
    LOCALE_KEY.test(k),
  );
}

/** A localized measure — `{ amount, units|unit: <locale-bag> }` (validity
 *  period, billing recurring period, service quantity). The unit is a
 *  translatable bag counted as content; the `amount` is a single shared number
 *  that still counts toward completion (filled when set). Returns the amount's
 *  filled state, or null when `node` isn't such a measure. */
function measureAmountState(node: unknown): boolean | null {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;
  const o = node as Record<string, unknown>;
  if (!("amount" in o)) return null;
  if (!isLocaleBag(o["units"]) && !isLocaleBag(o["unit"])) return null;
  return typeof o["amount"] === "number";
}

/** An icon / entertainment-logo attachment counts as ONE completion field —
 *  media, not translatable text. Filled when it has a url (string or any
 *  locale slot set). (#45/#46) */
function attachmentFilled(att: unknown): boolean {
  if (!att || typeof att !== "object") return false;
  const url = (att as Record<string, unknown>)["url"];
  if (typeof url === "string") return url.trim().length > 0;
  if (isLocaleBag(url)) {
    return Object.values(url as Record<string, unknown>).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
  }
  return false;
}

type PathSeg = string | number;

/** One collected completion field: either a locale bag (per-locale text) or a
 *  language-neutral entry (a measure amount or an attachment). `path` records
 *  where it lives so a blank field can be labelled and jumped to. */
interface CollectedField {
  path: PathSeg[];
  /** Set for a locale bag. */
  bag?: Record<string, unknown>;
  /** Set for a language-neutral field (measure amount / attachment). */
  neutralFilled?: boolean;
}

/** Recursively collect every localized field reachable from `node`. */
function collect(
  node: unknown,
  out: CollectedField[],
  seen: Set<object>,
  skipKeys: ReadonlySet<string>,
  path: PathSeg[],
): void {
  if (!node || typeof node !== "object") return;
  if (seen.has(node as object)) return;
  seen.add(node as object);

  if (isLocaleBag(node)) {
    out.push({ path, bag: node as Record<string, unknown> });
    return;
  }
  // A localized measure contributes its shared amount as a completion field —
  // but ONLY once the measure is "started" (its unit bag has content, or the
  // amount is set). A never-started measure (e.g. the quantity of an unlimited
  // service) is skipped so it isn't a phantom gap. Its unit bag, blank in every
  // locale, is likewise dropped by the optional-field rule during aggregation.
  const amountState = measureAmountState(node);
  if (amountState !== null) {
    const o = node as Record<string, unknown>;
    const unitBag = isLocaleBag(o["units"])
      ? (o["units"] as Record<string, unknown>)
      : isLocaleBag(o["unit"])
        ? (o["unit"] as Record<string, unknown>)
        : null;
    const unitStarted = unitBag
      ? Object.values(unitBag).some(
          (v) => typeof v === "string" && v.trim().length > 0,
        )
      : false;
    if (amountState === true || unitStarted) {
      out.push({ path, neutralFilled: amountState });
    }
  }

  if (Array.isArray(node)) {
    node.forEach((item, i) => collect(item, out, seen, skipKeys, [...path, i]));
    return;
  }
  for (const [key, val] of Object.entries(node as Record<string, unknown>)) {
    if (skipKeys.has(key)) continue;
    if (path.length === 0 && NON_CONTENT_TOP_LEVEL.has(key)) continue;
    // Attachments are media: count each image once and don't walk its
    // name/url/mimeType bags (else they'd inflate the count). (#45/#46)
    if (key === "attachment" && Array.isArray(val)) {
      val.forEach((att, i) =>
        out.push({
          path: [...path, key, i],
          neutralFilled: attachmentFilled(att),
        }),
      );
      continue;
    }
    // Service displays. Skip the pinned name/description mirrors (edited via the
    // price fields). Within a display, also skip the quantity/allowance
    // (`unitOfMeasure`) — an optional numeric detail, N/A for unlimited
    // services, so counting it re-flags an already-complete service. The
    // price-level validity `unitOfMeasure` (collected above) is unaffected.
    if (key === "displays" && Array.isArray(val)) {
      const displaySkip = new Set<string>([...skipKeys, "unitOfMeasure"]);
      val.forEach((disp, i) => {
        const dName = (disp as { name?: unknown })?.name;
        if (typeof dName === "string" && PINNED_DISPLAY_NAMES.has(dName))
          return;
        // Promo badge: counts only when Active (its label is required then); a
        // hidden badge isn't shown, so skip it entirely.
        if (dName === BADGE_DISPLAY_NAME) {
          const active =
            (disp as { value?: { isActive?: unknown } })?.value?.isActive ===
            true;
          if (!active) return;
        }
        collect(disp, out, seen, displaySkip, [...path, key, i]);
      });
      continue;
    }
    collect(val, out, seen, skipKeys, [...path, key]);
  }
}

/** Turn a collected field's path into a human label + a jump target. Falls back
 *  to a humanized last segment for any shape not explicitly mapped. */
function describeField(
  path: PathSeg[],
  detail: EnrichmentDetail,
): MissingField {
  const humanize = (s: string): string =>
    s
      .replaceAll(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (c) => c.toUpperCase());

  const priceIndex =
    path[0] === "prices" && typeof path[1] === "number"
      ? (path[1] as number)
      : undefined;

  if (priceIndex === undefined) {
    if (path[0] === "shelves")
      return { label: "Shelf name", tabId: "category", sectionId: "shelf" };
    if (path[0] === "agreements" || path[0] === "productOfferingTerm")
      return { label: "Terms & Conditions", tabId: "settings" };
    return {
      label: humanize(String(path[path.length - 1])),
      tabId: "price-config",
    };
  }

  const price = detail.prices?.[priceIndex];

  // Service Enrichment display sub-fields.
  if (path[2] === "displays" && typeof path[3] === "number") {
    const display = price?.displays?.[path[3] as number];
    if (display?.name === BADGE_DISPLAY_NAME) {
      return {
        label: "Badge label",
        tabId: "price-config",
        fieldKey: `displays.${BADGE_DISPLAY_NAME}.value.name`,
      };
    }
    const displayName =
      resolveLocalized((display?.value as { name?: unknown })?.name) ||
      display?.name ||
      "Service";
    const leaf = String(path[path.length - 1]);
    const part =
      leaf === "name"
        ? "name"
        : leaf === "description"
          ? "description"
          : leaf === "units" || path.includes("unitOfMeasure")
            ? "quantity"
            : leaf === "attachment" || typeof path[path.length - 1] === "number"
              ? "icon"
              : humanize(leaf);
    return {
      label: `${displayName} (${part})`,
      tabId: "price-config",
      // Only value.name carries a scroll anchor; using it also expands the card.
      fieldKey: display?.name
        ? `displays.${display.name}.value.name`
        : undefined,
    };
  }

  const seg = path[2];
  let suffix = humanize(String(path[path.length - 1]));
  let fieldKey: string | undefined;
  if (seg === "name") {
    suffix = "Product name";
    fieldKey = "name";
  } else if (seg === "description") {
    suffix = "Description";
    fieldKey = "description";
  } else if (seg === "price") {
    suffix = "Currency";
  } else if (seg === "unitOfMeasure") {
    suffix = "Validity";
  } else if (seg === "productOfferingTerm") {
    suffix = "Billing cycle";
  } else if (seg === "attachment") {
    suffix = "Image";
  }
  return { label: suffix, tabId: "price-config", fieldKey };
}

/** The localized fields that count even when entirely blank: a price's Product
 *  name and each service's name (matching the publish validator's required set).
 *  Every other localized field is optional — it only counts once the user has
 *  started translating it (so a never-touched optional field is neither a % drag
 *  nor a "still blank" list entry). The usage period is intentionally NOT here:
 *  an empty one is only a publish warning, and once started its gaps surface via
 *  the normal partial-translation path. */
function isRequiredBag(path: PathSeg[]): boolean {
  if (path[0] !== "prices" || typeof path[1] !== "number") return false;
  if (path.length === 3 && path[2] === "name") return true; // prices[i].name
  return (
    path[2] === "displays" &&
    path[path.length - 2] === "value" &&
    path[path.length - 1] === "name"
  ); // prices[i].displays[j].value.name
}

/**
 * Coverage across the editable localized fields in the flat `EnrichmentDetail`,
 * measured over the given active locale set. Required fields always count;
 * optional fields count only once started (so untouched optional fields — e.g.
 * the quantity of an unlimited service — aren't phantom gaps). Returns per-locale
 * fill stats plus the list of blank fields (label + jump target). Pure — no I/O.
 */
export function computeFlatLanguageCoverage(
  detail: EnrichmentDetail | null | undefined,
  locales: readonly string[],
): Record<string, FlatLangCoverage> {
  const result: Record<string, FlatLangCoverage> = {};
  for (const l of locales) {
    result[l] = { locale: l, filled: 0, total: 0, percent: 0, missing: [] };
  }
  if (!detail || locales.length === 0) return result;

  // `productOfferingTerm` (Billing cycle) is hidden for PREPAID, so drop it —
  // otherwise its recurring-period fields are a phantom gap the user can't fill.
  const isPrepaid =
    (detail.productType ?? "").toLowerCase().replaceAll(/[^a-z]/g, "") ===
    "prepaid";
  const skipKeys: ReadonlySet<string> = isPrepaid
    ? new Set<string>([...NON_CONTENT_KEYS, "productOfferingTerm"])
    : NON_CONTENT_KEYS;

  const fields: CollectedField[] = [];
  collect(detail, fields, new Set(), skipKeys, []);

  const hasText = (v: unknown): boolean =>
    typeof v === "string" && v.trim().length > 0;

  for (const field of fields) {
    if (field.bag) {
      const bag = field.bag;
      // "Started" = has content in ANY locale (even one outside the active set —
      // a value in a non-active language still means the field is in play, so
      // its active-locale gaps are real partial-translation gaps).
      const anyFilled = Object.values(bag).some((v) => hasText(v));
      // A never-started OPTIONAL field (blank in every active locale, not a
      // required field) is out of scope — it's not a gap the user must act on
      // (e.g. the quantity of an "unlimited" service), so it neither drags the %
      // down nor shows in the list. Required and partially-filled fields stay.
      if (!anyFilled && !isRequiredBag(field.path)) continue;
      for (const l of locales) {
        result[l].total += 1;
        if (hasText(bag[l])) result[l].filled += 1;
        else result[l].missing.push(describeField(field.path, detail));
      }
    } else {
      // Language-neutral (measure amount / attachment) — only "started" measures
      // reach here, so they count once toward every locale.
      for (const l of locales) {
        result[l].total += 1;
        if (field.neutralFilled === true) result[l].filled += 1;
        else result[l].missing.push(describeField(field.path, detail));
      }
    }
  }

  for (const l of locales) {
    const c = result[l];
    c.percent = c.total === 0 ? 0 : Math.round((c.filled / c.total) * 100);
  }
  return result;
}
